import * as React from 'react';
import {
  CaretUpDown,
  GitBranch,
  Plus,
  Check,
  SquaresFour,
} from '@phosphor-icons/react';
import { LogoPill } from '@/components/LogoPill';
import s from './project-switcher.module.scss';

export interface ProjectItem {
  _id: string;
  name: string;
  slug: string;
  kind?: string;
  repoFullName?: string;
}

export interface ProjectSwitcherProps {
  projects: ProjectItem[];
  selectedSlug: string | undefined;
  isAllProjects?: boolean;
  orgName?: string;
  onSelect: (slug: string) => void;
  onCreateProject?: () => void;
  createProjectLabel?: string;
  createProjectDescription?: string;
}

export function ProjectSwitcher({
  projects,
  selectedSlug,
  isAllProjects,
  orgName,
  onSelect,
  onCreateProject,
  createProjectLabel = 'New repository',
  createProjectDescription = 'Add another codebase',
}: ProjectSwitcherProps) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const selected = projects.find((p) => p.slug === selectedSlug);
  const displayName = isAllProjects
    ? 'All repositories'
    : (selected?.repoFullName ?? selected?.name ?? 'Select repository');
  const triggerMeta = isAllProjects ? 'Workspace view' : 'Active repository';

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className={s.switcher} ref={ref}>
      <button
        type="button"
        className={s.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <LogoPill size={24} />
        <div className={s.triggerText}>
          <span className={s.triggerMeta}>{triggerMeta}</span>
          <span className={s.triggerName}>{displayName}</span>
        </div>
        <CaretUpDown size={14} className={s.caret} />
      </button>

      {open && (
        <div className={s.dropdown}>
          {orgName && (
            <div className={s.dropdownHeader}>
              <LogoPill size={24} />
              <div className={s.dropdownHeaderText}>
                <span className={s.dropdownOrg}>{orgName}</span>
                <span className={s.dropdownHint}>Switch repository scope</span>
              </div>
            </div>
          )}

          <button
            type="button"
            className={s.option}
            data-active={isAllProjects || undefined}
            onClick={() => {
              onSelect('all');
              setOpen(false);
            }}
          >
            <SquaresFour size={14} weight="duotone" className={s.optionIcon} />
            <span className={s.optionName}>All repositories</span>
            {isAllProjects && (
              <Check size={12} weight="bold" className={s.checkIcon} />
            )}
          </button>

          <div className={s.divider} />
          <div className={s.dropdownLabel}>Repositories</div>

          {projects.map((p) => {
            const isActive = !isAllProjects && p.slug === selectedSlug;
            return (
              <button
                key={p._id}
                type="button"
                className={s.option}
                data-active={isActive || undefined}
                onClick={() => {
                  onSelect(p.slug);
                  setOpen(false);
                }}
              >
                <GitBranch
                  size={14}
                  weight="duotone"
                  className={s.optionIcon}
                />
                <div className={s.optionContent}>
                  <span className={s.optionName}>
                    {p.repoFullName ?? p.name}
                  </span>
                  {p.repoFullName && (
                    <span className={s.optionRepo}>{p.name}</span>
                  )}
                </div>
                {isActive && (
                  <Check size={12} weight="bold" className={s.checkIcon} />
                )}
              </button>
            );
          })}

          {onCreateProject && (
            <>
              <div className={s.divider} />
              <button
                type="button"
                className={s.createBtn}
                onClick={() => {
                  onCreateProject();
                  setOpen(false);
                }}
              >
                <span className={s.createIcon}>
                  <Plus size={13} weight="bold" />
                </span>
                <span className={s.createContent}>
                  <span className={s.optionName}>{createProjectLabel}</span>
                  <span className={s.optionRepo}>
                    {createProjectDescription}
                  </span>
                </span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
